import { useState } from "react";

import { refusalText } from "../../../../api/errors";
import { useApiCommand, useApiQuery } from "../../../../api/hooks";
import type { RegistrationCandidate, SymbolSetItem } from "../../../../api/types";
import { Button, ConfirmDialog } from "../../../../ui";
import { useCommandId, useRefresh } from "../../hooks";
import { Check, FormError, Select, TextArea, TextInput } from "../fields";
import { kindWord } from "./words";

/** After a mark, a registration or a withdrawal: the set and the pages that show the shape or the lines it names. */
const AFTER = ["symbol_set", "item_page", "item_snapshot", "element", "sme_card", "checks_list"] as const;

type Data = Record<string, unknown>;

function shapeData(item: SymbolSetItem): Data {
  return item.shape && typeof item.shape === "object" && !Array.isArray(item.shape) ? (item.shape as Data) : {};
}

function text(value: unknown): string {
  return typeof value === "string" ? value : "";
}

/** What a withdrawn shape is now, in plain words. */
export function withdrawnWords(item: SymbolSetItem): string {
  const after = item.replaced_by ? `, replaced by ${item.replaced_by}` : "";
  return item.origin === "loaded"
    ? `Proposed for withdrawal${after}. The library keeps it until the SME has looked; the proposal waits in the pending changes.`
    : `Withdrawn${after}. It stays in the files while an element still shows it, and cannot be picked again.`;
}

/**
 * The marks of a shape this discipline made (profile 2.21, section 5): the engineer's note to the SME, unsure, and
 * example content. A shape of the library carries none: only its origin and its discipline.
 */
export function ShapeMarks({ item, editable }: { item: SymbolSetItem; editable: boolean }) {
  const data = shapeData(item);
  const [note, setNote] = useState(text(data.engineer_note));
  const [unsure, setUnsure] = useState(data.unsure === "yes");
  const [example, setExample] = useState(data.example_content === "yes");
  const save = useApiCommand("set_shape_marks");
  const ids = useCommandId();
  const refresh = useRefresh();
  const own = item.origin === "app";
  const canEdit = editable && own && !item.withdrawn;
  const replaces = text(data.replaces);

  if (!own) {
    return (
      <section className="shape-about" aria-labelledby={`marks-${item.code}`}>
        <h4 id={`marks-${item.code}`}>About this shape</h4>
        <p className="muted">A shape of the library carries only where it comes from and its discipline. Write a note about it on the card of an element that shows it.</p>
      </section>
    );
  }
  const submit = () => {
    save.mutate(
      { command_id: ids.current(), code: item.code, engineer_note: note, unsure, example_content: example, expected_shape_version: item.shape_version },
      { onSuccess: () => { ids.renew(); refresh(...AFTER); }, onError: () => ids.renew() },
    );
  };
  return (
    <section className="shape-about" aria-labelledby={`marks-${item.code}`}>
      <h4 id={`marks-${item.code}`}>About this shape</h4>
      {replaces && <p className="muted">It replaces {replaces}, which was withdrawn.</p>}
      {canEdit ? (
        <div className="stack" role="group" aria-label={`The marks of ${item.code}`}>
          <TextArea label="Note to the SME" hint="Only the latest note is kept in the file. Leave it empty for none." value={note} onChange={setNote} />
          <Check label="Unsure as a whole" hint="The reason goes in the note." checked={unsure} onChange={setUnsure} />
          <Check label="Example content" hint="An example that is never shipped." checked={example} onChange={setExample} />
          <div className="row">
            <Button variant="secondary" busy={save.isPending} onClick={submit}>
              Save the marks
            </Button>
          </div>
          <FormError message={save.error ? refusalText(save.error) : null} />
        </div>
      ) : (
        <ul className="plain-list">
          <li>Note to the SME: {text(data.engineer_note) || "none"}</li>
          <li>Unsure as a whole: {data.unsure === "yes" ? "yes" : "no"}</li>
          <li>Example content: {data.example_content === "yes" ? "yes" : "no"}</li>
        </ul>
      )}
    </section>
  );
}

function candidateWords(c: RegistrationCandidate): string {
  if (c.line_registration) return c.line_registration;
  return [c.shape_name, c.registration_number, c.standard].filter(Boolean).join(", ");
}

/**
 * The registration of a shape: for a symbol the DEXPI shape name, the registration number and its standard; for a line
 * style its line registration. The app offers candidates only from what it has stored (DEC-1016-66), never from the
 * internet; the engineer keeps one, changes it or clears it.
 */
export function ShapeRegistration({ item, editable }: { item: SymbolSetItem; editable: boolean }) {
  const data = shapeData(item);
  const isSymbol = item.kind === "symbol";
  const reg = (data.symbol_registration && typeof data.symbol_registration === "object" ? data.symbol_registration : {}) as Data;
  const [shapeName, setShapeName] = useState(text(reg.shape_name));
  const [number, setNumber] = useState(text(reg.registration_number));
  const [standard, setStandard] = useState(text(reg.standard));
  const [line, setLine] = useState(text(data.line_registration));
  const canEdit = editable && !item.withdrawn;
  const candidates = useApiQuery("registration_candidates", { code: item.code }, { enabled: canEdit });
  const save = useApiCommand("set_shape_registration");
  const ids = useCommandId();
  const refresh = useRefresh();
  const shown = isSymbol ? [text(reg.shape_name), text(reg.registration_number), text(reg.standard)].filter(Boolean).join(", ") : text(data.line_registration);

  const send = (values: { shape_name?: string; registration_number?: string; standard?: string; line_registration?: string }) => {
    save.mutate(
      { command_id: ids.current(), code: item.code, expected_shape_version: item.shape_version, ...values },
      { onSuccess: () => { ids.renew(); refresh(...AFTER); }, onError: () => ids.renew() },
    );
  };
  const use = (c: RegistrationCandidate) => {
    if (isSymbol) {
      setShapeName(c.shape_name ?? "");
      setNumber(c.registration_number ?? "");
      setStandard(c.standard ?? "");
    } else {
      setLine(c.line_registration ?? "");
    }
  };
  const clear = () => {
    setShapeName("");
    setNumber("");
    setStandard("");
    setLine("");
    send(isSymbol ? { shape_name: "", registration_number: "", standard: "" } : { line_registration: "" });
  };

  return (
    <section className="shape-about" aria-labelledby={`registration-${item.code}`}>
      <h4 id={`registration-${item.code}`}>Registration</h4>
      {!canEdit ? (
        <p className="muted">{shown ? `Registered as ${shown}.` : "No registration is written for it."}</p>
      ) : (
        <div className="stack" role="group" aria-label={`The registration of ${item.code}`}>
          {item.origin === "loaded" && <p className="muted">A change to a shape of the library waits for the SME with an element that shows it.</p>}
          {isSymbol ? (
            <>
              <TextInput label="DEXPI shape name" value={shapeName} onChange={setShapeName} />
              <TextInput label="Registration number" hint="For example the ISO 10628 number." value={number} onChange={setNumber} />
              <TextInput label="Standard" hint="The standard the number belongs to, for example ISO 10628-2." value={standard} onChange={setStandard} />
            </>
          ) : (
            <TextInput label="Line registration" hint="The DEXPI or ISO 10628 name of the kind of line." value={line} onChange={setLine} />
          )}
          {candidates.isPending ? (
            <p className="muted">Looking in what the app has stored</p>
          ) : candidates.isError ? (
            <p className="muted">The stored candidates could not be read: {refusalText(candidates.error)}</p>
          ) : (candidates.data.candidates ?? []).length === 0 ? (
            <p className="muted">The app has nothing stored to offer for it. Type the registration if you know it.</p>
          ) : (
            <ul className="plain-list" aria-label={`What the app has stored for ${item.code}`}>
              {(candidates.data.candidates ?? []).map((c) => (
                <li key={`${candidateWords(c)}:${c.source}`}>
                  {candidateWords(c)} <span className="muted">(from {c.source})</span>{" "}
                  <Button variant="quiet" onClick={() => use(c)}>
                    Use {candidateWords(c)}
                  </Button>
                </li>
              ))}
            </ul>
          )}
          <div className="row">
            <Button
              variant="secondary"
              busy={save.isPending}
              onClick={() => send(isSymbol ? { shape_name: shapeName, registration_number: number, standard } : { line_registration: line })}
            >
              Save the registration
            </Button>
            {shown && (
              <Button variant="quiet" busy={save.isPending} onClick={clear}>
                Clear it
              </Button>
            )}
          </div>
          <FormError message={save.error ? refusalText(save.error) : null} />
        </div>
      )}
    </section>
  );
}

/**
 * Withdrawing a shape (profile 2.21): one this discipline made stays in the files while an element still shows it,
 * `replaces` on its successor names it, and the revision record keeps the withdrawal. A shape of the library is never
 * withdrawn here: the proposal waits in the pending changes.
 */
export function ShapeWithdrawal({ item, items, editable }: { item: SymbolSetItem; items: readonly SymbolSetItem[]; editable: boolean }) {
  const [successor, setSuccessor] = useState("");
  const [reason, setReason] = useState("");
  const [asking, setAsking] = useState(false);
  const withdraw = useApiCommand("withdraw_symbol");
  const ids = useCommandId();
  const refresh = useRefresh();
  if (!editable || item.withdrawn) return null;
  const word = kindWord(item.kind);
  const loaded = item.origin === "loaded";
  const options = items
    .filter((other) => other.kind === item.kind && other.code !== item.code && !other.withdrawn)
    .map((other) => ({ value: other.code, label: `${other.code} (${other.title})` }));
  const confirm = () => {
    withdraw.mutate(
      { command_id: ids.current(), code: item.code, replaced_by: successor || null, reason: reason.trim() || null, expected_shape_version: item.shape_version },
      {
        onSuccess: () => {
          ids.renew();
          setAsking(false);
          refresh(...AFTER);
        },
        onError: () => ids.renew(),
      },
    );
  };
  return (
    <section className="shape-about" aria-labelledby={`withdraw-${item.code}`}>
      <h4 id={`withdraw-${item.code}`}>{loaded ? "Propose to withdraw it" : `Withdraw this ${word}`}</h4>
      <p className="muted">
        {loaded
          ? `A ${word} of the library is never withdrawn here: the proposal waits for the SME in the pending changes, and the library keeps it meanwhile.`
          : `It stays in the files while an element still shows it, and its code is never used again. Give those elements another ${word}.`}
      </p>
      <div className="stack" role="group" aria-label={`Withdraw ${item.code}`}>
        <Select label="Replaced by" value={successor} placeholder={`No ${word} replaces it`} options={options} onChange={setSuccessor} />
        <TextArea label="Why" hint="Kept with the withdrawal (optional)." value={reason} onChange={setReason} rows={2} />
        <div className="row">
          <Button variant="quiet" onClick={() => setAsking(true)}>
            {loaded ? "Propose the withdrawal" : `Withdraw ${item.code}`}
          </Button>
        </div>
      </div>
      <ConfirmDialog
        open={asking}
        title={loaded ? `Propose to withdraw ${item.code}?` : `Withdraw ${item.code}?`}
        message={`${successor ? `${successor} replaces it. ` : ""}A withdrawal cannot be taken back here.`}
        confirmLabel={loaded ? "Yes, propose it" : "Yes, withdraw it"}
        busy={withdraw.isPending}
        error={withdraw.error ? refusalText(withdraw.error) : null}
        onConfirm={confirm}
        onCancel={() => setAsking(false)}
      />
    </section>
  );
}
